import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { CartProvider } from "@/lib/cart";
import { AuthProvider } from "@/lib/auth";
import { initAnalytics, trackPageview } from "@/lib/analytics";
import Layout from "@/components/layout/Layout";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import BookDetail from "@/pages/BookDetail";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import CheckoutCancel from "@/pages/CheckoutCancel";
import Admin from "@/pages/Admin";
// Manifesto page was removed to comply with Google Merchant Center misrepresentation policy.
// Old /manifesto URLs now redirect to /about.
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Privacy from "@/pages/Privacy";
import Returns from "@/pages/Returns";
import Shipping from "@/pages/Shipping";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Terms from "@/pages/Terms";
import FAQ from "@/pages/FAQ";
import Checkout from "@/pages/Checkout";
import TrackOrder from "@/pages/TrackOrder";
import Account from "@/pages/Account";
import CaseDetail from "@/pages/CaseDetail";
import CategoryLanding from "@/pages/CategoryLanding";
import LegacyShopRedirect from "@/pages/LegacyShopRedirect";
import "@/App.css";

function ScrollToTop() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [location.pathname, location.search]);
  return null;
}

// GA4: loads the tag once, then sends one page_view per route change.
// No-op until REACT_APP_GA4_MEASUREMENT_ID is set.
function AnalyticsTracker() {
  const location = useLocation();
  useEffect(() => {
    initAnalytics();
    // Defer one tick so the new page's document.title effect runs first
    const t = setTimeout(trackPageview, 0);
    return () => clearTimeout(t);
  }, [location]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Layout>
      <ScrollToTop />
      <AnalyticsTracker />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/shop" element={<LegacyShopRedirect />} />
        <Route path="/books" element={<CategoryLanding />} />
        <Route path="/books/*" element={<CategoryLanding />} />
        <Route path="/book/:id" element={<BookDetail />} />
        <Route path="/product/:slug" element={<BookDetail />} />
        <Route path="/manifesto" element={<Navigate to="/about" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/checkout/cancel" element={<CheckoutCancel />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/returns" element={<Returns />} />
        <Route path="/shipping" element={<Shipping />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/track/:orderId" element={<TrackOrder />} />
        <Route path="/account" element={<Account />} />
        <Route path="/cases/:caseId" element={<CaseDetail />} />
        <Route path="/admin" element={<Admin />} />
      </Routes>
    </Layout>
  );
}

export default function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.2, smoothWheel: true });
    let rafId = 0;
    function raf(t) {
      lenis.raf(t);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="bottom-center" />
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}
